import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import {
  ATTENDANCE_NOTIFICATION_ROLES,
  DEFAULT_ATTENDANCE_FINALIZATION_WORKFLOW,
  getDefaultAttendanceGeneralSettings,
  getScheduleLastRunMs,
  loadAttendanceGeneralSettings,
  saveAttendanceGeneralSettings,
  SCHEDULE_OPTIONS,
} from "../../../../../lib/attendanceGeneralSettings";
import { runAttendanceScheduleRecalc } from "../../../../../lib/attendanceScheduleRunner";
import { DsButton, DsCard } from "../../../../../design-system";
import LeaveWorkflowEditor from "../LeaveWorkflowEditor";

import { attendanceStructureService } from "../../../../../services/api";

const GeneralSettingsTab = ({
  darkMode,
  parentServiceCommencesOnDate,
  canEdit = true,
}) => {
  const [settings, setSettings] = useState(getDefaultAttendanceGeneralSettings);
  const [savedSnapshot, setSavedSnapshot] = useState(
    JSON.stringify(getDefaultAttendanceGeneralSettings())
  );
  const [runBusy, setRunBusy] = useState(false);
  const [lastRunLabel, setLastRunLabel] = useState("");

  const refreshLastRunLabel = () => {
    const ms = getScheduleLastRunMs();
    if (!ms) {
      setLastRunLabel("");
      return;
    }
    setLastRunLabel(new Date(ms).toLocaleString());
  };

  useEffect(() => {
    const loaded = loadAttendanceGeneralSettings();
    setSettings(loaded);
    setSavedSnapshot(JSON.stringify(loaded));
    refreshLastRunLabel();

    // Fetch from backend to ensure cross-device / company-level consistency
    attendanceStructureService
      .getAttendanceGeneralSettings()
      .then((res) => {
        if (res?.success && res?.data) {
          const remote = res.data;
          const merged = {
            ...loaded,
            runAttendanceSchedule:
              remote.runAttendanceSchedule || loaded.runAttendanceSchedule,
            notifyRoles: Array.isArray(remote.notifyRoles)
              ? remote.notifyRoles
              : loaded.notifyRoles,
            attendanceFinalization:
              remote.attendanceFinalization || loaded.attendanceFinalization,
          };
          saveAttendanceGeneralSettings(merged);
          setSettings(merged);
          setSavedSnapshot(JSON.stringify(merged));
        }
      })
      .catch(() => {});
  }, []);

  const serviceCommencementLabel =
    parentServiceCommencesOnDate &&
    parentServiceCommencesOnDate instanceof Date &&
    !isNaN(parentServiceCommencesOnDate.getTime())
      ? parentServiceCommencesOnDate.toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        })
      : "—";

  const isDirty = JSON.stringify(settings) !== savedSnapshot;

  const toggleNotifyRole = (roleValue) => {
    setSettings((prev) => {
      const has = prev.notifyRoles.includes(roleValue);
      const notifyRoles = has
        ? prev.notifyRoles.filter((r) => r !== roleValue)
        : [...prev.notifyRoles, roleValue];
      return { ...prev, notifyRoles };
    });
  };

  const handleSave = async () => {
    const saved = saveAttendanceGeneralSettings(settings);
    setSettings(saved);
    setSavedSnapshot(JSON.stringify(saved));
    try {
      await attendanceStructureService.saveAttendanceGeneralSettings(saved);
    } catch (e) {
      console.warn("Backend save attendance general settings warning:", e);
    }
    if (saved.runAttendanceSchedule !== "realtime") {
      runAttendanceScheduleRecalc({ reason: "manual", notify: true });
      refreshLastRunLabel();
    }
    toast.success("Attendance general settings saved");
  };

  const handleManualRun = () => {
    setRunBusy(true);
    try {
      runAttendanceScheduleRecalc({ reason: "manual_run", notify: true });
      refreshLastRunLabel();
      toast.success("Attendance status refresh started (same policy engine)");
    } catch (err) {
      console.warn("Manual attendance schedule run failed", err);
      toast.error("Could not run attendance refresh");
    } finally {
      setRunBusy(false);
    }
  };

  const labelClass = "ds-caption shrink-0 text-[hsl(var(--ds-muted-fg))]";
  const hintClass = "ds-small text-[hsl(var(--ds-muted-fg))]";
  const rowClass =
    "flex flex-nowrap items-center gap-3 overflow-x-auto pb-1 sm:gap-4";
  const sectionTitle = "ds-h4 shrink-0 text-[hsl(var(--ds-fg))]";

  return (
    <div className="space-y-6 p-4 lg:p-6">
      <div>
        <h3 className="ds-h3 mb-4 text-[hsl(var(--ds-fg))]">General Settings</h3>

        <DsCard padding="md" className="mb-4 !rounded-xl bg-[hsl(var(--ds-muted)/0.55)] shadow-none">
          <div className={rowClass}>
            <span className={labelClass}>Service Commencement On</span>
            <div
              className="shrink-0 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-4 py-2 text-sm text-[hsl(var(--ds-fg))]"
              title="Read-only: set at company level"
            >
              {serviceCommencementLabel}
            </div>
            <span className={`shrink-0 ${hintClass}`}>
              View-only · configured at superadmin level
            </span>
          </div>
        </DsCard>

        <DsCard padding="md" className="mb-4 !rounded-xl shadow-none">
          <div className={`${rowClass} mb-3 items-center justify-between gap-3`}>
            <div className="flex items-center gap-1.5">
              <span className={sectionTitle}>Attendance Finalization</span>
              <span
                title="Company-wide workflow configuration for Attendance Lock and Unlock requests. Applied across all policies."
                className="inline-flex h-3.5 w-3.5 cursor-help items-center justify-center text-[hsl(var(--ds-muted-fg))]"
                aria-label="Attendance finalization help"
              >
                ⓘ
              </span>
            </div>
          </div>
          <LeaveWorkflowEditor
            darkMode={darkMode}
            workflow={
              settings.attendanceFinalization ||
              DEFAULT_ATTENDANCE_FINALIZATION_WORKFLOW
            }
            onChange={(wf) =>
              setSettings((prev) => ({
                ...prev,
                attendanceFinalization: wf,
              }))
            }
            showTitle={false}
            showCancellationTab={false}
            isAttendanceFinalization={true}
            layout="inline"
          />
        </DsCard>

        <DsCard padding="md" className="mb-4 !rounded-xl shadow-none">
          <div className={`${rowClass} mb-3 justify-between gap-3`}>
            <span className={sectionTitle}>Run attendance schedule</span>
            <DsButton
              type="button"
              variant="secondary"
              size="sm"
              disabled={runBusy}
              onClick={handleManualRun}
              title="Re-evaluate PR/LOP from punches and policies now"
            >
              {runBusy ? "Running…" : "Run"}
            </DsButton>
          </div>
          <p className={`mb-3 ${hintClass}`}>
            Controls when PR/LOP is recalculated from punches and policies (same evaluation
            engine as check-in/out). Realtime updates on check-in/out; Daily refreshes about
            every 30 minutes; Weekly / Fortnightly / Monthly run a batch refresh on that
            interval. Use <strong>Run</strong> anytime to refresh status immediately without
            waiting for the schedule. Manual marking policies (Auto attendance + Override
            Attendance) keep day status as marked and are not overwritten by punch recalc.
            Saving a non-realtime option also runs one refresh immediately.
          </p>
          <div className="flex flex-wrap items-center gap-4 sm:gap-6">
            {SCHEDULE_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className="ds-body flex cursor-pointer items-center text-[hsl(var(--ds-fg))]"
              >
                <input
                  type="radio"
                  name="runAttendanceSchedule"
                  className="mr-2"
                  checked={settings.runAttendanceSchedule === opt.value}
                  onChange={() =>
                    setSettings((prev) => ({
                      ...prev,
                      runAttendanceSchedule: opt.value,
                    }))
                  }
                />
                {opt.label}
              </label>
            ))}
          </div>
          {lastRunLabel ? (
            <p className={`mt-3 ${hintClass}`}>Last run: {lastRunLabel}</p>
          ) : null}
        </DsCard>

        <DsCard padding="md" className="!rounded-xl shadow-none">
          <div className={`${rowClass} mb-2`}>
            <span className={sectionTitle}>Notify roles on recalculation</span>
          </div>
          <p className={`mb-3 ${hintClass}`}>
            When a scheduled or manual recalculation finishes, notify the selected roles.
          </p>
          <div className="flex flex-wrap gap-3 sm:gap-4">
            {ATTENDANCE_NOTIFICATION_ROLES.map((role) => (
              <label
                key={role.value}
                className="ds-body flex cursor-pointer items-center text-[hsl(var(--ds-fg))]"
              >
                <input
                  type="checkbox"
                  className="mr-2 rounded"
                  checked={settings.notifyRoles.includes(role.value)}
                  onChange={() => toggleNotifyRole(role.value)}
                />
                {role.label}
              </label>
            ))}
          </div>
        </DsCard>

        <div className="mt-6 flex justify-end">
          {canEdit !== false ? (
          <DsButton
            type="button"
            onClick={handleSave}
            disabled={!isDirty}
            variant="primary"
            size="md"
          >
            Save settings
          </DsButton>
          ) : (
            <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">View only</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default GeneralSettingsTab;
